<script>
import Button from 'primevue/button';
import Card from 'primevue/card';
import SelectButton from 'primevue/selectbutton';
import Password from 'primevue/password';

import SecurityService from '../../services/security';
import { required, minLength, helpers } from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";

const upperCaseLetter = helpers.withMessage('No upper case letter in password', function(el){
    return new RegExp('[A-Z]+').test(el)
});

const lowerCaseLetter = helpers.withMessage('No lower case letter in password', function(el){
    return new RegExp('[a-z]+').test(el)
});

const specialCharacter = helpers.withMessage('No Special character in password', function(el){
    return new RegExp('[!@#$%^&*°()_+=\\[{\\]};:<>|./?,-]').test(el)
});

const numberPresence = helpers.withMessage('No Number in password', function(el){
    return new RegExp('[0-9]+').test(el)
});

const confirmEqual = helpers.withMessage('Confirm password doesn\'t match', function(el){
    return el === this.data.confirmNewPassword;
});

export default{
    setup: () => ({ v$: useVuelidate() }),
    props: ['code'],
    components: {Button,Card,SelectButton,Password},
    data(){
        return{
            submitted: false,
            formData: {
                securityUsername: this.$route.params.username,
                oldPassword: '',
                newPassword: ''
            },
            data: {
                confirmNewPassword: ''
            },
            selectedLanguage: this.$store.state.security.language,
            languages: [ 
                {name: 'English', code: 'en'},
                {name: 'Italiano', code: 'it'}
            ],
        }
    },
     validations(){
           return {
                formData: {
                    securityUsername: {required},
                    oldPassword: {required, minLength: minLength(10)},
                    newPassword: {required, minLength: minLength(10), upperCaseLetter, lowerCaseLetter, specialCharacter, numberPresence, confirmEqual},
                },
                data: {
                    confirmNewPassword: {required},
                }
            }
        },
    mounted(){
    },
    methods: {
        setLanguage(e){
            this.$store.dispatch("security/setLanguage", e.value);
            this.$root.$i18n.changeLocale(this.$store.state.security.language);
        },
        onSubmit(valid) {
            this.submitted = true;
            if(valid){
                this.$store.dispatch("generic/setLoading", true);
                SecurityService.changePasswordExpired(this.formData, response => {
                    this.$store.dispatch("generic/setLoading", false);
                    this.submitted = false;
                    if(response.success){
                        this.$toast.add({severity:'success', summary: 'Success Message', detail: this.$t('messages.passwordResetSuccessMessage'), life: 3000});     
                        this.$router.push('/');
                    }else{
                        this.$toast.add({detail: response.message && response.message != '' ? response.message :  this.$t('messages.generic_error_message'), life:3000, severity:'error'});
                    }
                });
            }
        },
    }
};
</script>

<template>
<main>

    <Card style="width: 20rem; margin: 0 auto;">
        <template #title>
            {{$t('updatePassword')}}
        </template>
        <template #content>
            <form @submit.prevent="onSubmit(!v$.$invalid)">
                <div class="formgrid m-2">
                    <div class="my-3">
                        <span class="p-float-label" >
                            <InputText id="securityUsername" type="text" v-model="formData.securityUsername" :class="{'p-invalid':v$.formData.securityUsername.$invalid && submitted, 'w-full': true}"/>
                            <label for="securityUsername">{{$t('users.username')}}</label>
                        </span>
                        <small v-if="(v$.formData.securityUsername.$invalid && submitted) || v$.formData.securityUsername.$pending.$response" class="p-error">
                            {{v$.formData.securityUsername.required.$message.replace('Value', 'Username')}}
                        </small>
                    </div>
                    <div class="my-3">
                        <span class="p-float-label" >
                            <Password id="oldPassword" v-model="formData.oldPassword" :feedback="false" toggleMask :class="{'p-invalid':v$.formData.oldPassword.$invalid && submitted, 'w-full': true}"></Password>
                            <label for="oldPassword">{{$t('changePassword.oldPassword')}}</label>
                        </span>
                        
                        <div v-if="(v$.formData.oldPassword.$invalid && submitted) || v$.formData.oldPassword.$pending.$response">
                            <small class="p-error" v-for="error in v$.formData.oldPassword.$silentErrors" :key="error.$validator">
                                {{error.$message}}
                            </small>
                        </div>
                    </div>
                    <div class="my-3">
                        <span class="p-float-label" >
                            <Password id="newPassword" v-model="formData.newPassword" :feedback="false" toggleMask :class="{'p-invalid':v$.formData.newPassword.$invalid && submitted, 'w-full': true}"></Password>
                            <label for="newPassword">{{$t('changePassword.newPassword')}}</label>
                        </span>
                        <small v-if="(v$.formData.newPassword.$invalid && submitted) || v$.formData.newPassword.$pending.$response" class="p-error">
                            {{$t('changePassword.RulesNotMatched')}}
                        </small>
                    </div>
                    <div class="my-3">
                        <span class="p-float-label" >
                            <Password id="confirmNewPassword" v-model="data.confirmNewPassword" :feedback="false" toggleMask :class="{'p-invalid':v$.data.confirmNewPassword.$invalid && submitted, 'w-full': true}"></Password>
                            <label for="confirmNewPassword">{{$t('changePassword.confirmNewPassword')}}</label>
                        </span>
                        
                        <div v-if="(v$.data.confirmNewPassword.$invalid && submitted) || v$.data.confirmNewPassword.$pending.$response">
                            <small class="p-error" v-for="error in v$.data.confirmNewPassword.$silentErrors" :key="error.$validator">
                                {{error.$message}}
                            </small>
                        </div>
                    </div>
                    <div class="mt-4">
                        <h5 class="mb-2">{{$t('changePassword.newPasswordRules')}}</h5>
                        <ul class="mb-2">
                            <li><i :class="(!formData.newPassword || v$.formData.newPassword.minLength?.$invalid) ? 'text-red pi pi-stop' : 'text-green-400 pi pi-check-square'"></i>{{$t('changePassword.minLength')}}</li>
                            <li><i :class="(!formData.newPassword || v$.formData.newPassword.upperCaseLetter?.$invalid) ? 'text-red pi pi-stop' : 'text-green-400 pi pi-check-square'"></i>{{$t('changePassword.upperCaseLetter')}} </li>
                            <li><i :class="(!formData.newPassword || v$.formData.newPassword.lowerCaseLetter?.$invalid) ? 'text-red pi pi-stop' : 'text-green-400 pi pi-check-square'"></i>{{$t('changePassword.lowerCaseLetter')}}</li>
                            <li><i :class="(!formData.newPassword || v$.formData.newPassword.numberPresence?.$invalid) ? 'text-red pi pi-stop' : 'text-green-400 pi pi-check-square'"></i>{{$t('changePassword.numberPresence')}}</li>
                            <li><i :class="(!formData.newPassword || v$.formData.newPassword.specialCharacter?.$invalid) ? 'text-red pi pi-stop' : 'text-green-400 pi pi-check-square'"></i>{{$t('changePassword.specialCharacter')}}</li>
                            <li><i :class="(!formData.newPassword || v$.formData.newPassword.confirmEqual?.$invalid) ? 'text-red pi pi-stop' : 'text-green-400 pi pi-check-square'"></i>{{$t('changePassword.confirmEqual')}}</li>
                        </ul>
                    </div>
                    <div class="my-3" style="text-decoration: none;">
                        <router-link to="/login">{{$t('changePassword.returnLogin')}}</router-link>
                    </div>
                    <div style="text-align: center;">                         
                        <SelectButton v-model="selectedLanguage" :options="languages" optionLabel="name" optionValue="code" @change="setLanguage" />  
                    </div>
                </div>
            </form>
        </template>
    
         <template #footer> 
            <Button icon="pi pi-check" class="w-full" :label="$t('btn.updatePassword')" type="submit" @click="onSubmit(!v$.$invalid)" />
        </template>
    </Card>
</main>
</template>

<style scoped>

ul {
    list-style: none;
    padding: 0;
    margin: 0;
}

li {
    padding: 3px 0;
}

li > i{
    padding-right: 5px;
}
</style>